// Lightweight WebAudio engine. All sounds are synthesized at runtime — no
// asset files needed. The AudioContext is created lazily on the first user
// gesture (Chrome/Safari autoplay policy), and play() is a no-op until then.

export type SoundName =
  | 'step'
  | 'knock'
  | 'decoyThrow'
  | 'decoyPing'
  | 'ko'
  | 'detect'
  | 'suspicious'
  | 'capture'
  | 'win'
  | 'box';

// Per-sound rate limit (ms). Keeps state-transition pings from machine-gunning
// the speakers when an alert oscillates around the threshold.
const RATE_LIMIT_MS: Partial<Record<SoundName, number>> = {
  step: 120,
  detect: 250,
  suspicious: 250,
  knock: 100,
  ko: 100,
  box: 100,
  decoyPing: 80,
};
const DEFAULT_RATE_LIMIT_MS = 50;
const STEP_PERIOD_MS = 280;
const MASTER_GAIN = 0.4;

type AudioContextCtor = typeof AudioContext;

interface WebkitWindow {
  webkitAudioContext?: AudioContextCtor;
}

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? (window as unknown as WebkitWindow).webkitAudioContext ?? null;
}

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private lastPlayedAt = new Map<SoundName, number>();
  private stepTimer: ReturnType<typeof setInterval> | null = null;

  /** Create or resume the AudioContext. Safe to call multiple times. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = getAudioContextCtor();
    if (!Ctor) return;
    try {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
      this.master.connect(this.ctx.destination);
    } catch (err) {
      console.warn('AudioContext init failed', err);
    }
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
    if (this.muted) this.stopStep();
    return this.muted;
  }

  isReady(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  isMuted(): boolean {
    return this.muted;
  }

  play(name: SoundName): void {
    if (!this.ctx || !this.master || this.muted) return;
    const limit = RATE_LIMIT_MS[name] ?? DEFAULT_RATE_LIMIT_MS;
    const now = performance.now();
    const last = this.lastPlayedAt.get(name) ?? -Infinity;
    if (now - last < limit) return;
    this.lastPlayedAt.set(name, now);

    switch (name) {
      case 'step':
        this.synthStep();
        break;
      case 'knock':
        this.synthKnock();
        break;
      case 'decoyThrow':
        this.synthDecoyThrow();
        break;
      case 'decoyPing':
        this.synthDecoyPing();
        break;
      case 'ko':
        this.synthKO();
        break;
      case 'detect':
        this.synthDetect();
        break;
      case 'suspicious':
        this.synthSuspicious();
        break;
      case 'capture':
        this.synthCapture();
        break;
      case 'win':
        this.synthWin();
        break;
      case 'box':
        this.synthBox();
        break;
    }
  }

  /** Force-play bypassing the rate limiter (for tests). */
  resetRateLimit(): void {
    this.lastPlayedAt.clear();
  }

  startStep(): void {
    if (this.stepTimer !== null) return;
    this.play('step');
    this.stepTimer = setInterval(() => this.play('step'), STEP_PERIOD_MS);
  }

  stopStep(): void {
    if (this.stepTimer !== null) {
      clearInterval(this.stepTimer);
      this.stepTimer = null;
    }
  }

  // ===== synth helpers =====

  private envelope(node: GainNode, peak: number, duration: number, attack = 0.01): void {
    const t = this.ctx!.currentTime;
    node.gain.setValueAtTime(0, t);
    node.gain.linearRampToValueAtTime(peak, t + attack);
    node.gain.exponentialRampToValueAtTime(0.001, t + duration);
  }

  private tone(freq: number, duration: number, type: OscillatorType, peak: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    this.envelope(g, peak, duration);
    osc.connect(g).connect(this.master!);
    const t = ctx.currentTime;
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  private sweep(
    f0: number,
    f1: number,
    duration: number,
    type: OscillatorType,
    peak: number,
  ): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    const t = ctx.currentTime;
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + duration);
    this.envelope(g, peak, duration);
    osc.connect(g).connect(this.master!);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  private noise(
    duration: number,
    peak: number,
    filterFreq?: number,
    filterType: BiquadFilterType = 'lowpass',
  ): void {
    const ctx = this.ctx!;
    const buffer = ctx.createBuffer(1, Math.max(1, ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    this.envelope(g, peak, duration);
    let chain: AudioNode = src;
    if (filterFreq !== undefined) {
      const filter = ctx.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.value = filterFreq;
      chain.connect(filter);
      chain = filter;
    }
    chain.connect(g).connect(this.master!);
    src.start(ctx.currentTime);
  }

  // ===== preset synths =====

  private synthStep(): void {
    this.noise(0.04, 0.04, 800, 'lowpass');
  }

  private synthKnock(): void {
    this.tone(180, 0.22, 'sine', 0.35);
  }

  private synthDecoyThrow(): void {
    this.sweep(800, 200, 0.3, 'sine', 0.2);
  }

  private synthDecoyPing(): void {
    this.tone(1200, 0.08, 'sine', 0.18);
  }

  private synthKO(): void {
    this.noise(0.12, 0.25, 300, 'bandpass');
    this.tone(140, 0.06, 'sine', 0.2);
  }

  private synthDetect(): void {
    this.sweep(880, 440, 0.22, 'triangle', 0.4);
  }

  private synthSuspicious(): void {
    this.tone(880, 0.1, 'sine', 0.2);
  }

  private synthCapture(): void {
    this.sweep(400, 80, 0.8, 'sawtooth', 0.4);
  }

  private synthWin(): void {
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    notes.forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.18, 'sine', 0.3), i * 90);
    });
  }

  private synthBox(): void {
    this.noise(0.05, 0.15, 600, 'highpass');
  }
}

export const audio = new AudioEngine();
